// 3개월 분량 스킬 아이콘 경로(2026-10-27 GPT 콜라주 회차, tools/concept/jobs/skill-icons.codex.json). DOM·three import 금지(순수 경로 계산).
// 파일: public/assets/ui/skills/<스킬 id에서 '.'을 '_'로>.webp (예 kael.job30.a → kael_job30_a.webp), 각성 <영웅>_awaken.webp,
// 전직 문장 job_<영웅>_<a|b>.webp. 정사각 어두운 배경 타일이라 칸을 꽉 채워(cover) 그린다.
// 지금 플레이 가능한 스킬(타임라인 번호)의 승인 그림(ui/skill-hud-v2/<영웅>-<번호>)은 skillArt.ts가 그대로 쓴다 — 이 모듈은
// 그 그림이 없는 자리(HEROES_90D 26개 로드맵·각성·전직 문장)에 쓴다.
import { HEROES_90D } from '../shared/content/heroes';
import type { HeroDef, HeroId, SkillUnlock } from '../shared/content/types';
import { heroSkills } from '../shared/skills/registry';
import type { PlayableHeroId } from '../shared/types';

/** 각성 아이콘을 가리키는 스킬 id(HEROES_90D skills에는 없다, HeroDef.awaken 이름) */
export const AWAKEN_SKILL_ID = 'awaken';

function heroDef(hero: HeroId): HeroDef | undefined { return HEROES_90D.find((h) => h.id === hero); }

/**
 * 영웅 + 스킬 id → 그림 경로(assetUrl 전). skillId: HEROES_90D 스킬 id(kael.base.0·kael.active5·kael.job30.a·kael.awaken250.b·
 * kael.ultimate60·kael.master65) 또는 'awaken'(·'kael.awaken'). 그 영웅 표에 없는 id는 null(없는 그림 주소를 만들지 않는다)
 */
export function skillIconPath(heroId: HeroId, skillId: string): string | null {
  const h = heroDef(heroId);
  if (!h) return null;
  if (skillId === AWAKEN_SKILL_ID || skillId === heroId + '.' + AWAKEN_SKILL_ID) return `ui/skills/${heroId}_awaken.webp`;
  if (!h.skills.some((s) => s.id === skillId)) return null;
  return 'ui/skills/' + skillId.replace(/\./g, '_') + '.webp';
}

/** 전직 갈래 문장(0 = HeroDef.jobs[0] → a, 1 → b) */
export function jobEmblemPath(heroId: HeroId, job: 0 | 1): string {
  return `ui/skills/job_${heroId}_${job === 0 ? 'a' : 'b'}.webp`;
}

/** 영웅의 아이콘 26개 id(기본 3·액티브·전직 a/b·2차 각성 a/b·궁극·마스터 7 = 25 + 각성) */
export function skillIconIds(heroId: HeroId): string[] {
  const h = heroDef(heroId);
  return h ? [...h.skills.map((s) => s.id), AWAKEN_SKILL_ID] : [];
}

/**
 * 지금 플레이 가능한 스킬(타임라인 번호 1..n) → HEROES_90D 스킬 id. 같은 이름의 줄로 잇는다(카엘 4 진홍 초승달 = kael.active10).
 * 이름이 바뀌어 못 찾으면 null
 */
export function skill90dIdOf(hero: PlayableHeroId, timelineId: number, job: 0 | 1 = 0): string | null {
  const t = heroSkills(hero, job).find((s) => s.id === timelineId);
  const h = heroDef(hero);
  if (!t || !h) return null;
  return h.skills.find((s) => s.name === t.name)?.id ?? null;
}

/** 로드맵 한 칸(스킬 도감): 레벨 순서, 전직 갈래 스킬은 job 0/1 */
export interface SkillRoadCell {
  readonly id: string;
  readonly name: string;
  readonly level: number;
  readonly kind: SkillUnlock['kind'] | 'awaken';
  readonly job: 0 | 1 | null;
  readonly icon: string;
  /** 배운 스킬: 레벨 스킬은 level 이하, 전직 갈래 스킬(kind 'job')은 그 단계 전직을 마쳤고 고른 갈래일 때만(C10-5) */
  readonly learned: boolean;
}

/** 스킬 도감 칸(26개): 각성은 맨 앞(기본 습득 각성 게이지 스킬) */
/** job = 전직 상태(C10-5, GameView.job: tier 0 전직 전·1·2, branch). 없으면 전직 전으로 본다 */
export function skillRoad(heroId: HeroId, level: number, job: { readonly tier: number; readonly branch: number } | null = null): SkillRoadCell[] {
  const h = heroDef(heroId);
  if (!h) return [];
  const out: SkillRoadCell[] = [{ id: AWAKEN_SKILL_ID, name: h.awaken, level: 1, kind: 'awaken', job: null, icon: skillIconPath(heroId, AWAKEN_SKILL_ID)!, learned: true }];
  for (const s of h.skills) out.push({ id: s.id, name: s.name, level: s.level, kind: s.kind, job: s.job ?? null, icon: skillIconPath(heroId, s.id)!, learned: s.kind === 'job' ? !!job && job.branch === s.job && job.tier >= (s.level >= 40 ? 2 : 1) : s.level <= level });
  return out;
}
